import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HeartPulse, QrCode, Users, LogOut, Plus, ExternalLink, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    
    if (!token) {
      navigate("/login");
      return;
    }

    if (userData) {
      setUser(JSON.parse(userData));
    }

    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      } else {
        toast.error("Failed to load dashboard stats");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateBracelets = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/bracelets/generate?count=50`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Generated 50 new bracelets!");
      fetchStats();
    } catch (error) {
      toast.error("Failed to generate bracelets");
    } finally {
      setGenerating(false);
    }
  };

  const setupInitialData = async () => {
    try {
      await axios.post(`${API}/setup/admin`);
      await axios.post(`${API}/setup/bracelets`);
      toast.success("Initial setup complete!");
      fetchStats();
    } catch (error) {
      console.log("Setup may already exist");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto"
            />
            <span className="font-bold text-xl text-slate-900 hidden sm:inline" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SOS Dashboard
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">
              {user?.name} {user?.is_admin && <span className="text-teal-600 font-medium">(Admin)</span>}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-500 hover:text-red-500"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container-app py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200 card-hover" data-testid="stat-total-bracelets">
            <CardHeader className="pb-2">
              <CardDescription>Total Bracelets</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.total_bracelets || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <QrCode className="h-4 w-4" />
                <span>QR codes generated</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 card-hover" data-testid="stat-registered">
            <CardHeader className="pb-2">
              <CardDescription>Registered</CardDescription>
              <CardTitle className="text-3xl font-bold text-teal-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.registered_bracelets || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4" />
                <span>Active profiles</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 card-hover" data-testid="stat-unregistered">
            <CardHeader className="pb-2">
              <CardDescription>Unregistered</CardDescription>
              <CardTitle className="text-3xl font-bold text-amber-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.unregistered_bracelets || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <QrCode className="h-4 w-4" />
                <span>Available for use</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 card-hover" data-testid="stat-profiles">
            <CardHeader className="pb-2">
              <CardDescription>Total Profiles</CardDescription>
              <CardTitle className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.total_profiles || 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4" />
                <span>People protected</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-200 card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <QrCode className="h-5 w-5 text-teal-600" />
                Manage Bracelets
              </CardTitle>
              <CardDescription>View and download QR codes for bracelets</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/dashboard/bracelets">
                <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="view-bracelets-btn">
                  View All Bracelets
                </Button>
              </Link>
              <Link to="/dashboard/print-qr">
                <Button variant="outline" className="w-full" data-testid="print-qr-btn">
                  <Printer className="mr-2 h-4 w-4" />
                  Print QR Codes
                </Button>
              </Link>
              {user?.is_admin && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={generateBracelets}
                  disabled={generating}
                  data-testid="generate-bracelets-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Generate 50 More
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Users className="h-5 w-5 text-teal-600" />
                Manage Profiles
              </CardTitle>
              <CardDescription>View and edit registered profiles</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/dashboard/profiles">
                <Button className="w-full bg-teal-600 hover:bg-teal-700" data-testid="view-profiles-btn">
                  View All Profiles
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="border-slate-200 card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <ExternalLink className="h-5 w-5 text-teal-600" />
                Main Website
              </CardTitle>
              <CardDescription>Visit stressfreeaging.com</CardDescription>
            </CardHeader>
            <CardContent>
              <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full" data-testid="visit-main-site-btn">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Visit Website
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Initial Setup Button (only show if no bracelets) */}
        {stats?.total_bracelets === 0 && user?.is_admin && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Initial Setup Required
              </CardTitle>
              <CardDescription className="text-amber-700">
                Generate the initial 500 bracelets to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={setupInitialData}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="setup-initial-btn"
              >
                Run Initial Setup (500 Bracelets)
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
